import React, { useContext, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Button, Card, CardBody, Col, CustomInput, Form, FormGroup, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Row, Table } from 'reactstrap'
import toastr from 'toastr'
import ApiService from '../../../data/services/ApiService'
import { GeneralContext } from '../../../context/'
import MaterialEmojiPicker from './MaterialEmojiPicker'
import { missingConfiguration } from '../readiness'

const isImageIcon = value => /^https?:\/\//i.test(value || '')
const empty = { id: '', code: '', name: '', description: '', icon: '', canFrame: false, canMariaLuisa: false, availableForCatalog: true, availableForPersonalized: true, isActive: false }
const capabilities = { canFrame: 'Permite enmarcado', canMariaLuisa: 'Permite María Luisa', availableForCatalog: 'Disponible en catálogo', availableForPersonalized: 'Disponible para personalizados' }

const Readiness = ({ readiness, configuration, shipping = false }) => {
    const ready = shipping ? readiness?.hasShippingProfiles : readiness?.hasPrices
    const missing = shipping ? readiness?.missingShippingProfiles : readiness?.missingPrices
    if (ready === undefined || !Array.isArray(missing)) return <span className="text-muted">Sin información</span>
    if (ready) return <span className="text-success">Completo</span>
    const counts = missingConfiguration(readiness, configuration, shipping)
    return <div className="text-warning">
        Incompleto: {missing.length} pendientes
        {counts ? <small className="d-block">{counts.absent} sin configurar · {counts.inactive} inactivos</small>
            : <small className="d-block">Faltantes o inactivos; detalle no disponible.</small>}
    </div>
}

const MaterialsManager = () => {
    const { setLoading } = useContext(GeneralContext)
    const [items, setItems] = useState([])
    const [details, setDetails] = useState({})
    const [form, setForm] = useState(null)
    const [iconMode, setIconMode] = useState('emoji')
    const [emojiDraft, setEmojiDraft] = useState('')
    const [imageDraft, setImageDraft] = useState('')
    const [file, setFile] = useState(null)
    const [preview, setPreview] = useState('')
    useEffect(() => {
        if (!file) { setPreview(''); return }
        const url = URL.createObjectURL(file)
        setPreview(url)
        return () => URL.revokeObjectURL(url)
    }, [file])
    const selectIcon = event => {
        const selected = event.target.files?.[0]
        event.target.value = ''
        if (!selected) return
        if (!['image/png', 'image/jpeg', 'image/webp'].includes(selected.type)) return toastr.error('Solo se aceptan imágenes PNG, JPEG o WebP')
        if (!selected.size || selected.size > 50 * 1024 * 1024) return toastr.error('La imagen debe pesar entre 1 byte y 50 MB')
        setFile(selected)
    }
    const edit = value => {
        setFile(null)
        setIconMode(isImageIcon(value.icon) ? 'image' : 'emoji')
        setEmojiDraft(isImageIcon(value.icon) ? '' : value.icon || '')
        setImageDraft(isImageIcon(value.icon) ? value.icon : '')
        setForm(value)
    }
    const [saving, setSaving] = useState(false)
    const [publication, setPublication] = useState(null)
    const load = async () => {
        try {
            setLoading(true)
            const response = await ApiService.getMaterials()
            const materials = response.data.materials || []
            setItems(materials)
            setDetails({})
            const configurations = await Promise.all(materials.filter(item => item.readiness && (!item.readiness.hasPrices || !item.readiness.hasShippingProfiles)).map(async item => {
                try { return [item.id, (await ApiService.getMaterialConfiguration(item.id)).data] }
                catch (_) { return [item.id, null] }
            }))
            setDetails(Object.fromEntries(configurations))
        } catch (_) { toastr.error('No pude cargar los materiales') }
        finally { setLoading(false) }
    }
    useEffect(() => { load() }, []) // eslint-disable-line
    const saveMaterial = async event => {
        event.preventDefault()
        if (saving) return
        if (!form.code.trim() || !form.name.trim()) return toastr.error('Código y nombre son requeridos')
        if (Array.from((iconMode === 'emoji' ? emojiDraft : imageDraft) || '').length > 255) return toastr.error('El ícono o emoji debe tener como máximo 255 caracteres')
        try {
            setSaving(true)
            setLoading(true)
            let icon = (iconMode === 'emoji' ? emojiDraft : imageDraft) || ''
            if (iconMode === 'image' && file) {
                const data = new FormData()
                // Keep storage URLs short enough for the existing VARCHAR(255).
                data.append('file', file, `material-icon.${file.type === 'image/jpeg' ? 'jpg' : file.type.split('/')[1]}`)
                const uploaded = await ApiService.uploadHeroImage(data)
                if (!isImageIcon(uploaded.data.url) || uploaded.data.url.length > 255) throw new Error('No pude cargar el ícono del material')
                icon = uploaded.data.url
            }
            // Send only model metadata; never readiness, prices or shipping data.
            const metadata = Object.fromEntries(['code', 'name', 'description', 'icon', ...Object.keys(capabilities), 'isActive'].map(field => [field, form[field]]))
            metadata.icon = icon
            if (form.id) {
                const response = await ApiService.updateMaterial(form.id, metadata)
                setItems(current => current.map(item => item.id === form.id ? response.data.material : item))
            } else await ApiService.createMaterial({ ...metadata, isActive: false })
            toastr.success('Material guardado')
            setFile(null)
            setForm(null)
            if (!form.id) await load()
        } catch (error) { toastr.error(error.response?.data?.message || error.message || 'No pude guardar el material') }
        finally { setSaving(false); setLoading(false) }
    }
    const changePublication = async () => {
        if (saving || !publication) return
        try {
            setSaving(true)
            // PUT expects the full metadata payload; preserve capabilities and channels.
            const response = await ApiService.updateMaterial(publication.id, { ...publication, isActive: !publication.isActive })
            setItems(current => current.map(item => item.id === publication.id ? response.data.material : item))
            toastr.success(publication.isActive ? 'Material despublicado' : 'Material publicado')
            setPublication(null)
        } catch (error) {
            const data = error.response?.data
            const readiness = data?.readiness
            const pending = [
                ...(readiness?.priceRequirements || []).filter(row => row.status !== 'active').map(row => `Precio ${row.materialCode} ${row.size} (${row.status === 'inactive' ? 'inactivo' : 'faltante'})`),
                ...(readiness?.shippingProfileRequirements || []).filter(row => row.status !== 'active').map(row => `Envío ${row.size} / ${row.marialuisaMode} (${row.status === 'inactive' ? 'inactivo' : 'faltante'})`),
            ]
            toastr.error([data?.message || 'No pude cambiar la publicación del material', ...pending,
                ...(readiness ? ['Completa y activa la configuración desde Configurar (CSV) y vuelve a intentar.'] : [])].join('. '))
        } finally { setSaving(false) }
    }
    const closePublication = () => { if (!saving) setPublication(null) }
    const toggle = field => setForm({ ...form, [field]: !form[field] })
    const close = () => { if (!saving) { setFile(null); setForm(null) } }
    return <React.Fragment>
        <Card><CardBody>
            <div className="d-flex flex-wrap justify-content-between align-items-center mb-3">
                <p className="text-muted mb-2">Crea materiales, edita sus datos y consulta su estado. Configura precios y envíos mediante CSV.</p>
                <Button color="primary" className="mb-2" onClick={() => edit({ ...empty })}>Crear material</Button>
            </div>
            <Table striped responsive>
                <thead><tr><th>Material</th><th>Precios</th><th>Envíos</th><th>Publicación</th><th>Acciones</th></tr></thead>
                <tbody>{items.map(item => <tr key={item.id}>
                    <td>{isImageIcon(item.icon) ? <img src={item.icon} alt="" style={{ width: 32, height: 32, objectFit: 'contain' }} /> : item.icon} {item.name}<br /><small>{item.code}</small></td>
                    <td><Readiness readiness={item.readiness} configuration={details[item.id]} /></td>
                    <td><Readiness readiness={item.readiness} configuration={details[item.id]} shipping /></td>
                    <td>{item.isActive ? 'Activo / publicado' : 'Inactivo / borrador'}</td>
                    <td><Button tag={Link} size="sm" color="primary" className="mr-2 mb-2" to={`/products/bulk?tab=pricing&material=${encodeURIComponent(item.code)}`}>Configurar</Button>
                        <Button size="sm" color="secondary" className="mr-2 mb-2" disabled={saving} onClick={() => edit({ ...empty, ...item })}>Editar datos</Button>
                        <Button size="sm" color={item.isActive ? 'warning' : 'success'} className="mb-2" disabled={saving} onClick={() => setPublication(item)}>{item.isActive ? 'Despublicar' : 'Publicar'}</Button></td>
                </tr>)}</tbody>
            </Table>
            {items.length === 0 && <p className="text-muted">No hay materiales para mostrar.</p>}
        </CardBody></Card>
        <Modal fade={false} isOpen={Boolean(form)} toggle={close} size="lg" labelledBy="material-title">
            {form && <Form onSubmit={saveMaterial}>
                <ModalHeader toggle={saving ? undefined : close} id="material-title">{form.id ? 'Editar material' : 'Crear material'}</ModalHeader>
                <ModalBody>
                    <Row><Col md={6}><FormGroup><Label for="material-code">Código técnico *</Label><Input id="material-code" required disabled={Boolean(form.id) || saving} value={form.code} onChange={e => setForm({ ...form, code: e.target.value })} /></FormGroup></Col>
                        <Col md={6}><FormGroup><Label for="material-name">Nombre *</Label><Input id="material-name" required disabled={saving} value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} /></FormGroup></Col></Row>
                    <FormGroup><Label for="material-description">Descripción</Label><Input id="material-description" type="textarea" disabled={saving} value={form.description || ''} onChange={e => setForm({ ...form, description: e.target.value })} /></FormGroup>
                    <FormGroup><Label for="material-icon-mode">Ícono del material</Label>
                        <Input id="material-icon-mode" type="select" value={iconMode} disabled={saving} onChange={e => setIconMode(e.target.value)}>
                            <option value="emoji">Emoji</option><option value="image">Imagen personalizada</option>
                        </Input>
                        <small className="text-muted d-block">Solo se usa el modo seleccionado. Los cambios se aplican al guardar; Cancelar conserva el ícono guardado.</small>
                        {iconMode === 'emoji' ? <MaterialEmojiPicker value={emojiDraft} onChange={setEmojiDraft} disabled={saving} /> : <div>
                            <Label className="mt-2" for="material-icon">Archivo de imagen</Label>
                            <Input id="material-icon" type="file" accept="image/png,image/jpeg,image/webp" disabled={saving} onChange={selectIcon} aria-describedby="material-icon-help" />
                            <small id="material-icon-help" className="text-muted d-block">PNG, JPEG o WebP. Máximo 50 MB. La imagen se carga al guardar.</small>
                            {(preview || imageDraft) ? <img src={preview || imageDraft} alt="Vista previa del ícono del material" style={{ width: 80, height: 80, objectFit: 'contain' }} /> : <p>Sin imagen</p>}
                            {(file || imageDraft) && <Button type="button" color="link" disabled={saving} onClick={() => { setFile(null); setImageDraft('') }}>Quitar ícono</Button>}
                        </div>}
                    </FormGroup>
                    {Object.entries(capabilities).map(([field, label]) => <CustomInput className="mb-2" key={field} id={`material-${field}`} type="switch" label={label} checked={form[field]} disabled={saving} onChange={() => toggle(field)} />)}
                    <small className="text-muted d-block">Desactiva Permite María Luisa si el material no admite esta opción.</small>
                    <small className="text-muted d-block mt-2">Los materiales nuevos se crean como borrador. Usa Publicar o Despublicar en la lista para cambiar su publicación con confirmación.</small>
                    <small className="text-muted d-block mt-2">La disponibilidad por canal y la publicación son independientes. El servidor valida los requisitos de publicación al guardar.</small>
                </ModalBody>
                <ModalFooter><Button type="button" color="secondary" disabled={saving} onClick={close}>Cancelar</Button><Button color="primary" type="submit" disabled={saving}>{saving ? 'Guardando…' : 'Guardar'}</Button></ModalFooter>
            </Form>}
        </Modal>
        {/* Bootstrap 3 .fade conflicts with Bootstrap 4 .show; omit transitions. */}
        <Modal fade={false} isOpen={Boolean(publication)} toggle={closePublication} labelledBy="material-publication-title">
            <ModalHeader id="material-publication-title" toggle={saving ? undefined : closePublication}>Confirmar {publication?.isActive ? 'despublicación' : 'publicación'}</ModalHeader>
            <ModalBody>
                <p>¿{publication?.isActive ? 'Despublicar' : 'Publicar'} el material <strong>{publication?.name}</strong> ({publication?.code})?</p>
                <p>{publication?.isActive ? 'Dejará de estar disponible para nuevas compras. Los pedidos existentes, precios y perfiles de envío se conservarán.' : 'Estará disponible en los canales habilitados. El servidor comprobará que todos los precios y perfiles de envío requeridos estén completos y activos.'}</p>
            </ModalBody>
            <ModalFooter>
                <Button color="secondary" disabled={saving} onClick={closePublication}>Cancelar</Button>
                <Button color="primary" disabled={saving} onClick={changePublication}>{saving ? 'Guardando…' : 'Confirmar'}</Button>
            </ModalFooter>
        </Modal>
    </React.Fragment>
}
export default MaterialsManager
